import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as BookingService from "_services/booking-service";
import * as UserService from "_services/user-service";



const initialState = {
  departures: null,
  arrivals: null,
  dateFilter: null,
  userInfo: null,
  getUserStatus: "idle",
  dateFilterEnabled: false,
  editBooking: null,
  type: "departures",
  editBookingStatus: false,
  selectedRequests: [],
  confirmedRequests: [],
  airlineRequests: [],
  confirmedDepartures: [],
  confirmedAirlines: [],
  status: "idle",
  sortByDateCreated: "flight-descending",
  deleteStatus: "idle",
  patchStatus: "idle",
  // bookings the server refused to confirm (outside this user's airport/airline)
  patchSkipped: [],
  confirmedStatus: "idle",
  confirmedError: null,
  patchError: null,
  deleteError: null,
  error: null,
  skipObj: {
    confirmedArrivals: 0,
    confirmedDepartures: 0,
    pendingDepartures: 0,
    pendingArrivals: 0,
    airlineRequests: 0,
    airlineConfirmedRequests: 0,
  },
  statusObj: {},
  autoConfirm: false,
  totalRows: 0
};

function searchMatch(request, text) {
  if (!text) {
    return true;
  }

  let flightNumber = (
    request.doc.flightDetails.carrierCode +
    request.doc.flightDetails.flightNumber
  ).toLowerCase();
  let name = request.doc.name.toLowerCase();
  let departureDate = request.doc.flightDetails.departureDate.toLowerCase();
  // app bookings may have no referenceNumber on the doc; the row carries the resolved one
  let referenceNumber = String(request.referenceNumber || request.doc.referenceNumber || "").toLowerCase();

  return [flightNumber, name, departureDate, referenceNumber].some((val) =>
    val.includes(text.toLowerCase())
  );
}



export const getUserInfo = createAsyncThunk(
  "bookingRequests/getUserInfo",
  async (userId) => {
    return UserService.getUserByUserId(userId);
  }
);

/* get all departure requests */
export const getDepartures = createAsyncThunk(
  "bookingRequests/getDepartures",
  async (params) => {
    return BookingService.getRequests(params);
  }
);

/* get all arrivals requests */
export const getArrivals = createAsyncThunk(
  "bookingRequests/getArrivals",
  async (params) => {
    return BookingService.getRequests(params);
  }
);

/* get all requests for airline */
export const getAirlineRequests = createAsyncThunk(
  "bookingRequests/getRequests",
  async (params) => {
    return BookingService.getAirlineRequests(params);
  }
);

/* patch list of bookings */
export const patchRequests = createAsyncThunk(
  "bookingRequests/patchRequests",
  async (params) => {
    return BookingService.patchRequests(params);
  }
);

/* delete request from list */
export const deleteRequest = createAsyncThunk(
  "bookingRequests/deleteRequest",
  async (params) => {
    return BookingService.deleteBooking(params);
  }
);

/*  */
export const getConfirmedArrivals = createAsyncThunk(
  "bookingRequests/getConfirmedArrivals",
  async (params) => {
    return BookingService.getConfirmedRequests(params);
  }
);

/*  */
export const getConfirmedDepartures = createAsyncThunk(
  "bookingRequests/getConfirmedDepartures",
  async (params) => {
    return BookingService.getConfirmedRequests(params);
  }
);

export const getConfirmedAirlines = createAsyncThunk(
  "bookingRequests/getConfirmedAirlines",
  async (params) => {
    return BookingService.getConfirmedRequests(params);
  }
);

const bookingRequestsTableSlice = createSlice({
  name: "requests",
  initialState,
  reducers: {
    reset: (state) => {
      return { ...initialState, autoConfirm: state.autoConfirm };
    },
    setSelectedRequests: (state, action) => {
      return {
        ...state,
        selectedRequests: action.payload,
      };
    },
    clearList: (state, action) => {
      return {
        ...state,
        selectedRequests: [],
      };
    },
    setDateFilter: (state, action) => {
      return {
        ...state,
        dateFilter: action.payload,
      };
    },
    setDateFilterEnabled: (state, action) => {
      return {
        ...state,
        dateFilterEnabled: action.payload,
      };
    },
    setEditBooking: (state, action) => {
      return {
        ...state,
        editBooking: action.payload,
      };
    },
    setEditBookingStatus: (state, action) => {
      return {
        ...state,
        editBookingStatus: action.payload,
      };
    },
    setType: (state, action) => {
      return {
        ...state,
        type: action.payload,
      };
    },
    setSortByDateCreated: (state, action) => {
      return {
        ...state,
        sortByDateCreated: action.payload,
      };
    },

    setSkip: (state, action) => {
      let newSkip = state.skipObj[action.payload.type] + action.payload.change;
      newSkip = newSkip < 0 ? 0 : newSkip;
      return {
        ...state,
        skipObj: { ...state.skipObj, [action.payload.type]: newSkip },
      };
    },
    resetSkip: (state, action) => {
      return {
        ...state,
        skipObj: { ...state.skipObj, [action.payload]: 0 },
      };
    },
    setAutoConfirm: (state, action) => {
      return {
        ...state,
        autoConfirm: action.payload,
      };
    },
  },
  extraReducers: {
    [getDepartures.pending]: (state, action) => {
      return {
        ...state,
        status: "loading",
        statusObj: { ...state.statusObj, getDepartures: "loading" },
      };
    },
    [getDepartures.fulfilled]: (state, action) => {
      return {
        ...state,
        status: "succeeded",
        statusObj: { ...state.statusObj, getDepartures: "succeeded" },
        departures: action.payload.rows,
        totalRows: action.payload.totalRows
      };
    },
    [getDepartures.rejected]: (state, action) => {
      return {
        ...state,
        status: "failed",
        statusObj: { ...state.statusObj, getDepartures: "failed" },
        error: action.error.message,
      };
    },
    [getArrivals.pending]: (state, action) => {
      return {
        ...state,
        status: "loading",
        statusObj: { ...state.statusObj, getArrivals: "loading" },
      };
    },
    [getArrivals.fulfilled]: (state, action) => {
      return {
        ...state,
        status: "succeeded",
        statusObj: { ...state.statusObj, getArrivals: "succeeded" },
        arrivals: action.payload.rows,
        totalRows: action.payload.totalRows
      };
    },
    [getArrivals.rejected]: (state, action) => {
      return {
        ...state,
        status: "failed",
        statusObj: { ...state.statusObj, getArrivals: "failed" },
        error: action.error.message,
      };
    },
    [getAirlineRequests.pending]: (state, action) => {
      return {
        ...state,
        status: "loading",
        statusObj: { ...state.statusObj, getAirlineRequests: "loading" },
      };
    },
    [getAirlineRequests.fulfilled]: (state, action) => {

      return {
        ...state,
        status: "succeeded",
        statusObj: { ...state.statusObj, getAirlineRequests: "succeeded" },
        airlineRequests: action.payload.rows,
        totalRows: action.payload.totalRows
      };
    },
    [getAirlineRequests.rejected]: (state, action) => {
      return {
        ...state,
        status: "failed",
        statusObj: { ...state.statusObj, getAirlineRequests: "failed" },
        error: action.error.message,
      };
    },
    [patchRequests.pending]: (state, action) => {
      return {
        ...state,
        patchStatus: "loading",
        patchSkipped: [],
      };
    },
    [patchRequests.fulfilled]: (state, action) => {
      // new response: { confirmed: string[], skipped: [{ id, reason }] }
      // old response: a plain string — treat as nothing skipped
      const skipped = Array.isArray(action.payload?.skipped) ? action.payload.skipped : [];
      return {
        ...state,
        patchStatus: "succeeded",
        patchSkipped: skipped,
      };
    },
    [patchRequests.rejected]: (state, action) => {
      return {
        ...state,
        patchStatus: "failed",
        patchError: action.error.message,
      };
    },
    [deleteRequest.pending]: (state, action) => {
      return {
        ...state,
        deleteStatus: "loading",
      };
    },
    [deleteRequest.fulfilled]: (state, action) => {
      return {
        ...state,
        deleteStatus: "succeeded",
      };
    },
    [deleteRequest.rejected]: (state, action) => {
      return {
        ...state,
        deleteStatus: "failed",
        patchError: action.error.message,
      };
    },
    [getConfirmedArrivals.pending]: (state, action) => {
      return {
        ...state,
        confirmedStatus: "loading",
        statusObj: { ...state.statusObj, getConfirmedArrivals: "loading" },
      };
    },
    [getConfirmedArrivals.fulfilled]: (state, action) => {
      return {
        ...state,
        confirmedStatus: "succeeded",
        confirmedArrivals: action.payload.rows,
        totalRows: action.payload.totalRows,
        statusObj: { ...state.statusObj, getConfirmedArrivals: "succeeded" },
      };
    },
    [getConfirmedArrivals.rejected]: (state, action) => {
      return {
        ...state,
        confirmedStatus: "failed",
        confirmedError: action.error.message,
        statusObj: { ...state.statusObj, getConfirmedArrivals: "failed" },
      };
    },
    [getConfirmedDepartures.pending]: (state, action) => {
      return {
        ...state,
        confirmedStatus: "loading",
        statusObj: { ...state.statusObj, getConfirmedDepartures: "loading" },
      };
    },
    [getConfirmedDepartures.fulfilled]: (state, action) => {
      return {
        ...state,
        confirmedStatus: "succeeded",
        statusObj: { ...state.statusObj, getConfirmedDepartures: "succeeded" },
        confirmedDepartures: action.payload.rows,
        totalRows: action.payload.totalRows
      };
    },
    [getConfirmedDepartures.rejected]: (state, action) => {
      return {
        ...state,
        confirmedStatus: "failed",
        confirmedError: action.error.message,
        statusObj: { ...state.statusObj, getConfirmedDepartures: "failed" },
      };
    },
    [getConfirmedAirlines.pending]: (state, action) => {
      return {
        ...state,
        confirmedStatus: "loading",
        statusObj: { ...state.statusObj, getConfirmedAirlines: "loading" },
      };
    },
    [getConfirmedAirlines.fulfilled]: (state, action) => {
      return {
        ...state,
        confirmedStatus: "succeeded",
        confirmedAirlines: action.payload.rows,
        totalRows: action.payload.totalRows,
        statusObj: { ...state.statusObj, getConfirmedAirlines: "succeeded" },
      };
    },
    [getConfirmedAirlines.rejected]: (state, action) => {
      return {
        ...state,
        confirmedStatus: "failed",
        confirmedError: action.error.message,
        statusObj: { ...state.statusObj, getConfirmedAirlines: "failed" },
      };
    },
    [getUserInfo.pending]: (state, action) => {
      return {
        ...state,
        getUserStatus: "loading",
      };
    },
    [getUserInfo.fulfilled]: (state, action) => {
      return {
        ...state,
        getUserStatus: "succeeded",
        userInfo: action.payload,
      };
    },
    [getUserInfo.rejected]: (state, action) => {
      return {
        ...state,
        getUserStatus: "failed",
      };
    },
  },
});



export const {
  setDateFilterEnabled,
  setEditBookingStatus,
  reset,
  resetSkip,
  setSelectedRequests,
  clearList,
  setDateFilter,
  setEditBooking,
  setType,
  setSortByDateCreated,
  setSkip,
  setAutoConfirm,
} = bookingRequestsTableSlice.actions;

export const selectRequestsDepartures = state => state.requests.departures;
export const selectRequestsArrivals = state => state.requests.arrivals;
export const selectConfirmedArrivals = state => state.requests.confirmedArrivals;
export const selectConfirmedDepartures = state => state.requests.confirmedDepartures;
export const selectConfirmedAirlines = state => state.requests.confirmedAirlines;
export const selectRequestsStatus = (state) => state.requests.status;
export const selectPatchStatus = (state) => state.requests.patchStatus;
export const selectPatchSkipped = (state) => state.requests.patchSkipped || [];
export const selectDeleteStatus = (state) => state.requests.deleteStatus;
export const selectEditBooking = (state) => state.requests.editBooking;
export const selectEditBookingStatus = (state) => state.requests.editBookingStatus;
export const selectType = (state) => state.requests.type;
export const selectUserInfo = (state) => state.requests.userInfo;
export const selectDateFilter = (state) => state.requests.dateFilterEnabled;
export const selectSelectedRequests = (state) => state.requests.selectedRequests;
export const selectDepartures = (state) => state.requests.departures;
export const selectArrivals = (state) => state.requests.arrivals;
export const selectAirlineRequests = (state) => state.requests.airlineRequests;
export const selectConfirmedStatus = (state) => state.requests.confirmedStatus;
export const selectSortByDateCreated = (state) => state.requests.sortByDateCreated;
export const selectStatusObj = (state) => state.requests.statusObj;
export const selectAutoConfirm = (state) => state.requests.autoConfirm;

export const selectTotalRows = state => state.requests.totalRows;
export const selectSkipObj = state => state.requests.skipObj || initialState.skipObj
export default bookingRequestsTableSlice.reducer;
